import React, { useMemo } from 'react';
import type { EntryPointProps } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import SpotlightHeaderImage from '@atlassian/jira-illustrations/src/ui/adg4/jira/heroes/components/this-is-new-jira/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { INVITE_USERS_PROMPTS_KEY, JSM_ADOPTION_NUDGES_PACKAGE } from '../../common/constants.tsx';
import messages from './messages.tsx';
import type { InviteUsersPromptProps } from './types.tsx';

// TODO implement the spotlight card ui
const InviteUsersPromptNudge = ({ props }: EntryPointProps<{}, {}, InviteUsersPromptProps, {}>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { onFinish, onCTAClick } = props;

	const nudge = useMemo(
		() => ({
			actions: [
				{
					onClick: () => {
						fireUIAnalytics(
							createAnalyticsEvent({}),
							'button clicked',
							'contextualInviteUserPromptButtonInviteTeam',
						);
						onFinish();
						onCTAClick();
					},
					text: expValEquals('jsm_core_invites_experiment', 'cohort', 'experiment')
						? formatMessage(messages.addPeopleButton)
						: formatMessage(messages.inviteButton),
				},
				{
					onClick: () => {
						fireUIAnalytics(
							createAnalyticsEvent({}),
							'button clicked',
							'contextualInviteUserPromptButtonDismiss',
						);
						onFinish();
					},
					text: formatMessage(messages.dismissButton),
					'aria-label': formatMessage(messages.dismissButtonAriaLabel),
				},
			],
			message: formatMessage(
				fg('itsm-issue-renaming') ? messages.descriptionIssueTermRefresh : messages.description,
			),
		}),
		[formatMessage, createAnalyticsEvent, onFinish, onCTAClick],
	);

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName={JSM_ADOPTION_NUDGES_PACKAGE}>
			<JiraSpotlight
				actions={nudge.actions}
				dialogPlacement="right bottom"
				dialogWidth={275}
				targetRadius={3}
				target={INVITE_USERS_PROMPTS_KEY}
				key={INVITE_USERS_PROMPTS_KEY}
				heading={formatMessage(
					fg('itsm-issue-renaming') ? messages.headingIssueTermRefresh : messages.heading,
				)}
				messageId="service-management-adoption-nudges.ui.invite-user-prompt.jira-spotlight"
				messageType="engagement"
			>
				<Box xcss={spotlightStyles}>
					<SpotlightHeaderImage alt="" />
				</Box>
				{nudge.message}
			</JiraSpotlight>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};
export default InviteUsersPromptNudge;

const spotlightStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	transform: 'translateY(-152px)',
	backgroundColor: 'color.background.discovery.bold',
	left: 'space.0',
	borderTopLeftRadius: '3px',
	borderTopRightRadius: '3px',
});
